// src/pages/Reportes.jsx
import React, { useState, useMemo, useEffect } from "react";
import { useCotizaciones } from "../hooks/useCotizaciones";
import { useRol, useUsuarios } from "../hooks/useRoles";
import { useHistorial } from "../hooks/useHistorial";
import { exportarReporteExcel } from "../utils/exportExcel";

const MESES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

const ESTADO_STYLE = {
  "Pendiente":  { bg:"#E6F1FB", color:"#185FA5" },
  "Aprobada":   { bg:"#EAF3DE", color:"#3B6D11" },
  "Rechazada":  { bg:"#FCEBEB", color:"#A32D2D" },
  "Vencida":    { bg:"#FAEEDA", color:"#854F0B" },
  "Ganada":     { bg:"#E1F5EE", color:"#0F6E56" },
  "Perdida":     { bg:"#FCEBEB", color:"#A32D2D" },
};

const fmtMoney    = v => `S/ ${Number(v||0).toLocaleString("es-PE")}`;
const fmtMoneyUSD = v => `$ ${Number(v||0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtFecha = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("es-PE", { day:"numeric", month:"short", year:"numeric" });
};

function calcStats(cots) {
  const cerradas   = cots.filter(c => ["Ganada","Cerrada","Aprobada"].includes(c.estado));
  const perdidas   = cots.filter(c => c.estado === "Perdida");
  const pipeline   = cots.filter(c => !["Ganada","Cerrada","Aprobada","Perdida","Rechazada","Vencida"].includes(c.estado));
  const ingresos   = cerradas.reduce((s,c) => s+(Number(c.valor)||0), 0);
  const perdidoVal = perdidas.reduce((s,c) => s+(Number(c.valor)||0), 0);
  const pipelineVal= pipeline.reduce((s,c) => s+(Number(c.valor)||0), 0);
  const total      = cots.length;
  const conversion = total > 0 ? +((cerradas.length/total)*100).toFixed(1) : 0;
  const ticketProm = cerradas.length > 0 ? Math.round(ingresos/cerradas.length) : 0;
  return { cerradas, perdidas, pipeline, ingresos, perdidoVal, pipelineVal, total, conversion, ticketProm };
}

function trendPct(curr, prev) {
  if (!prev || prev === 0) return null;
  return +((curr - prev) / prev * 100).toFixed(0);
}

function Trend({ curr, prev }) {
  const t = trendPct(curr, prev);
  if (t === null) return null;
  return (
    <span style={{ fontSize: 9, fontWeight: 700, color: t >= 0 ? "#1D9E75" : "#DC2626" }}>
      {t >= 0 ? "▲" : "▼"} {Math.abs(t)}% vs anterior
    </span>
  );
}

function KPI({ icono, label, valor, sub, subColor, curr, prev }) {
  return (
    <div className="kpi-card card-lift" style={{ background:"#fff", border:"0.5px solid #e8ecf3", borderRadius:12, padding:"14px 16px" }}>
      <div style={{ fontSize:16, marginBottom:4 }}>{icono}</div>
      <div style={{ fontSize:10, color:"#64748b", fontWeight:500, marginBottom:3 }}>{label}</div>
      <div style={{ fontSize:19, fontWeight:800, color:"#1e293b", letterSpacing:"-0.4px" }}>{valor}</div>
      {sub && <div style={{ fontSize:10, marginTop:3, color:subColor||"#1D9E75", fontWeight:500 }}>{sub}</div>}
      {curr !== undefined && prev !== undefined && <div style={{ marginTop:4 }}><Trend curr={curr} prev={prev} /></div>}
    </div>
  );
}

function BarraFunnel({ label, count, total, color, mounted, delay = 0 }) {
  const pct = total > 0 ? (count/total)*100 : 0;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
      <div style={{ fontSize:11, color:"#475569", width:85, flexShrink:0 }}>{label}</div>
      <div style={{ flex:1, background:"#f1f5f9", borderRadius:4, height:20, overflow:"hidden" }}>
        <div style={{
          width: mounted ? `${pct}%` : "0%",
          height:"100%", background:color, borderRadius:4,
          display:"flex", alignItems:"center", paddingLeft:6,
          minWidth: mounted && count > 0 ? 24 : 0,
          transition: `width 600ms cubic-bezier(0.23,1,0.32,1) ${delay}ms`,
        }}>
          {count > 0 && mounted && <span style={{ fontSize:10, fontWeight:600, color:"#fff" }}>{count}</span>}
        </div>
      </div>
      <div style={{ fontSize:11, fontWeight:600, color:"#1e293b", width:20, textAlign:"right" }}>{count}</div>
    </div>
  );
}

const AV_COLORS = ["#EEEDFE|#534AB7","#E1F5EE|#0F6E56","#FAEEDA|#854F0B","#E6F1FB|#185FA5","#EAF3DE|#3B6D11"];

export default function Reportes() {
  const { cotizaciones, loading }     = useCotizaciones();
  const { esAdmin, loading:rolLoad }  = useRol();
  const { usuarios }                  = useUsuarios();
  const { historial, loading:histLoad, actualizarHistorial, eliminarHistorial } = useHistorial();

  const [mounted,       setMounted]       = useState(false);
  const [periodo,       setPeriodo]       = useState("todo");
  const [fechaDesde,    setFechaDesde]    = useState("");
  const [fechaHasta,    setFechaHasta]    = useState("");
  const [filtroAsesor,  setFiltroAsesor]  = useState("todos");
  const [editId,        setEditId]        = useState(null);
  const [editComent,    setEditComent]    = useState("");
  const [confirmDel,    setConfirmDel]    = useState(null);
  const [showDetalle,   setShowDetalle]   = useState(false);
  const [busqDetalle,   setBusqDetalle]   = useState("");

  useEffect(() => { const t = setTimeout(() => setMounted(true), 60); return () => clearTimeout(t); }, []);

  const ahora = new Date();

  const asesores = useMemo(() => (
    (usuarios||[]).filter(u => u.activo !== false).map(u => u.nombre).sort()
  ), [usuarios]);

  const estaEnRango = (ts, p = periodo) => {
    if (!ts) return true;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (p === "mes")       return d.getMonth()===ahora.getMonth() && d.getFullYear()===ahora.getFullYear();
    if (p === "trimestre") { const diff=(ahora.getFullYear()-d.getFullYear())*12+(ahora.getMonth()-d.getMonth()); return diff<=3; }
    if (p === "anio")      return d.getFullYear()===ahora.getFullYear();
    if (p === "custom" && fechaDesde && fechaHasta) {
      const [dy,dm,dd]=fechaDesde.split("-").map(Number); const desde=new Date(dy,dm-1,dd);
      const [hy,hm,hd]=fechaHasta.split("-").map(Number); const hasta=new Date(hy,hm-1,hd,23,59,59);
      return d>=desde && d<=hasta;
    }
    return true;
  };

  // Rango anterior para comparativas
  const estaEnRangoAnterior = (ts) => {
    if (!ts) return false;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (periodo === "mes") {
      const ini = new Date(ahora.getFullYear(), ahora.getMonth()-1, 1);
      const fin = new Date(ahora.getFullYear(), ahora.getMonth(), 0, 23, 59, 59);
      return d>=ini && d<=fin;
    }
    if (periodo === "trimestre") {
      const diff=(ahora.getFullYear()-d.getFullYear())*12+(ahora.getMonth()-d.getMonth());
      return diff>3 && diff<=6;
    }
    if (periodo === "anio") return d.getFullYear()===ahora.getFullYear()-1;
    return false;
  };

  const cotFiltradas = useMemo(() => (
    (cotizaciones||[])
      .filter(c => estaEnRango(c.creadoEn))
      .filter(c => filtroAsesor==="todos" || c.responsable===filtroAsesor)
  ), [cotizaciones, periodo, fechaDesde, fechaHasta, filtroAsesor]);

  const cotAnteriores = useMemo(() => (
    (cotizaciones||[])
      .filter(c => estaEnRangoAnterior(c.creadoEn))
      .filter(c => filtroAsesor==="todos" || c.responsable===filtroAsesor)
  ), [cotizaciones, periodo, filtroAsesor]);

  const historialFiltrado = useMemo(() => (
    (historial||[])
      .filter(h => estaEnRango(h.fecha))
      .filter(h => filtroAsesor==="todos" || h.responsable===filtroAsesor)
  ), [historial, periodo, fechaDesde, fechaHasta, filtroAsesor]);

  const stats  = useMemo(() => calcStats(cotFiltradas),  [cotFiltradas]);
  const statsP = useMemo(() => calcStats(cotAnteriores), [cotAnteriores]);

  // Ganancia por moneda — cotizaciones Ganadas separadas por PEN y USD
  const gananciaMoneda = useMemo(() => {
    const ganadas = cotFiltradas.filter(c => ["Ganada","Cerrada","Aprobada"].includes(c.estado));
    const pen = ganadas.filter(c => !c.moneda || c.moneda === "PEN");
    const usd = ganadas.filter(c => c.moneda === "USD");
    const totalPEN = pen.reduce((s,c)  => s + (Number(c.valor)||0), 0);
    const totalUSD = usd.reduce((s,c)  => s + (Number(c.valor)||0), 0);
    return { pen: { total: totalPEN, count: pen.length }, usd: { total: totalUSD, count: usd.length } };
  }, [cotFiltradas]);

  const porEstado = useMemo(() => (
    ["Pendiente","Aprobada","Ganada","Perdida","Rechazada","Vencida"].map(est => ({
      estado: est,
      count: cotFiltradas.filter(c => {
        const e = c.estado || "Pendiente";
        return e === est || (est === "Ganada" && e === "Cerrada");
      }).length,
    }))
  ), [cotFiltradas]);

  // Ingresos + conversión por mes (últimos 6)
  const ingresosMes = useMemo(() => (
    Array.from({length:6}, (_,i) => {
      const d    = new Date(ahora.getFullYear(), ahora.getMonth()-5+i, 1);
      const mes  = d.getMonth();
      const anio = d.getFullYear();
      const cotsDelMes = (cotizaciones||[])
        .filter(c => filtroAsesor==="todos" || c.responsable===filtroAsesor)
        .filter(c => {
          if (!c.creadoEn) return false;
          const f = c.creadoEn.toDate ? c.creadoEn.toDate() : new Date(c.creadoEn);
          return f.getMonth()===mes && f.getFullYear()===anio;
        });
      const val  = cotsDelMes.filter(c=>c.estado==="Ganada").reduce((s,c)=>s+(Number(c.valor)||0),0);
      const conv = cotsDelMes.length>0 ? +((cotsDelMes.filter(c=>c.estado==="Ganada").length/cotsDelMes.length)*100).toFixed(0) : 0;
      return { mes:MESES[mes], val, conv, isActual:i===5 };
    })
  ), [cotizaciones, filtroAsesor]);

  const maxVal = useMemo(() => Math.max(...ingresosMes.map(m=>m.val),1), [ingresosMes]);

  const porAsesor = useMemo(() => {
    const map = {};
    cotFiltradas.forEach(c => {
      const r = c.responsable||"Sin asignar";
      if (!map[r]) map[r]={nombre:r,total:0,cerradas:0,valor:0};
      map[r].total++;
      if (c.estado==="Ganada") { map[r].cerradas++; map[r].valor+=Number(c.valor)||0; }
    });
    return Object.values(map).sort((a,b)=>b.valor-a.valor);
  }, [cotFiltradas]);

  // Top 5 clientes por ingresos en el período
  const topClientes = useMemo(() => {
    const map = {};
    cotFiltradas.filter(c=>c.estado==="Ganada").forEach(c => {
      if (!c.clienteNombre) return;
      if (!map[c.clienteNombre]) map[c.clienteNombre]={nombre:c.clienteNombre,empresa:c.clienteEmpresa||"",total:0,count:0};
      map[c.clienteNombre].total+=Number(c.valor)||0;
      map[c.clienteNombre].count++;
    });
    return Object.values(map).sort((a,b)=>b.total-a.total).slice(0,5);
  }, [cotFiltradas]);

  const proyectos = useMemo(() => (
    [...cotFiltradas]
      .filter(c=>c.proyecto&&c.proyecto.trim()!=="")
      .sort((a,b)=>(Number(b.valor)||0)-(Number(a.valor)||0))
      .slice(0,5)
  ), [cotFiltradas]);

  const estancadas = useMemo(() => {
    const limite = new Date(); limite.setDate(limite.getDate()-7);
    return [...cotFiltradas]
      .filter(c=>!["Ganada","Cerrada","Aprobada","Perdida","Rechazada","Vencida"].includes(c.estado)&&c.actualizadoEn)
      .filter(c=>{
        const f=c.actualizadoEn?.toDate?c.actualizadoEn.toDate():new Date(c.actualizadoEn||0);
        return f<limite;
      })
      .sort((a,b)=>{
        const fa=a.actualizadoEn?.toDate?a.actualizadoEn.toDate():new Date(a.actualizadoEn||0);
        const fb=b.actualizadoEn?.toDate?b.actualizadoEn.toDate():new Date(b.actualizadoEn||0);
        return fa-fb;
      })
      .slice(0,5);
  }, [cotFiltradas]);

  // Tabla detalle filtrada
  const detalleRows = useMemo(() => {
    let lista = [...cotFiltradas];
    if (busqDetalle) {
      const b = busqDetalle.toLowerCase();
      lista = lista.filter(c =>
        c.clienteNombre?.toLowerCase().includes(b) ||
        c.clienteEmpresa?.toLowerCase().includes(b) ||
        c.numeroCot?.toLowerCase().includes(b) ||
        c.proyecto?.toLowerCase().includes(b)
      );
    }
    return lista;
  }, [cotFiltradas, busqDetalle]);

  const mostrarTendencia = ["mes","trimestre","anio"].includes(periodo);

  if (rolLoad||loading) return <div style={{textAlign:"center",padding:60,color:"#94a3b8",fontSize:14}}>Cargando reportes...</div>;
  if (!esAdmin) return <div style={{textAlign:"center",padding:60,color:"#94a3b8",fontSize:14}}>No tienes permiso.</div>;

  const card      = { background:"#fff", border:"0.5px solid #e8ecf3", borderRadius:12, padding:16 };
  const cardTitle = { fontSize:11, fontWeight:700, color:"#1e293b", textTransform:"uppercase", letterSpacing:".05em", marginBottom:12, display:"flex", alignItems:"center", justifyContent:"space-between" };

  const PERIODOS = [
    {key:"mes",label:"Este mes"},{key:"trimestre",label:"3 meses"},
    {key:"anio",label:"Este año"},{key:"todo",label:"Todo"},{key:"custom",label:"Personalizado"},
  ];

  return (
    <div style={{maxWidth:1100,margin:"0 auto",padding:"0 4px"}}>

      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:8}}>
        <div>
          <h1 style={{fontSize:22,fontWeight:700,color:"#1e293b"}}>Informes</h1>
          <div style={{fontSize:12,color:"#64748b",marginTop:2}}>Solo administradores · Datos en tiempo real</div>
        </div>
        <button
          onClick={() => exportarReporteExcel({clientes:cotFiltradas,historial:historialFiltrado,stats:{...stats,cerrados:stats.cerradas,perdidos:stats.perdidas},porAsesor:porAsesor.map(a=>({...a,cerrados:a.cerradas})),periodo})}
          style={{padding:"8px 18px",borderRadius:8,background:"#1D9E75",color:"#fff",border:"none",fontSize:13,fontWeight:700,cursor:"pointer"}}
        >
          ⬇ Exportar Excel
        </button>
      </div>

      {/* Filtros */}
      <div style={{background:"#fff",border:"0.5px solid #e2e8f0",borderRadius:10,padding:"12px 16px",display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <span style={{fontSize:11,fontWeight:600,color:"#64748b"}}>Período:</span>
        {PERIODOS.map(p=>{
          const on=periodo===p.key;
          return (
            <button key={p.key} onClick={()=>setPeriodo(p.key)} style={{
              padding:"5px 12px",borderRadius:16,fontSize:11,fontWeight:600,
              border:`1.5px solid ${on?"#3A3C7A":"#e2e8f0"}`,
              background:on?"#3A3C7A":"#fff",color:on?"#fff":"#64748b",cursor:"pointer",
            }}>{p.label}</button>
          );
        })}
        {periodo==="custom" && (
          <>
            <input type="date" value={fechaDesde} onChange={e=>setFechaDesde(e.target.value)} style={{padding:"5px 10px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:12}}/>
            <span style={{fontSize:11,color:"#94a3b8"}}>—</span>
            <input type="date" value={fechaHasta} onChange={e=>setFechaHasta(e.target.value)} style={{padding:"5px 10px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:12}}/>
          </>
        )}
        <div style={{width:"0.5px",height:20,background:"#e2e8f0",margin:"0 4px"}}/>
        <span style={{fontSize:11,fontWeight:600,color:"#64748b"}}>Asesor:</span>
        <select value={filtroAsesor} onChange={e=>setFiltroAsesor(e.target.value)}
          style={{padding:"5px 10px",border:"1px solid #e2e8f0",borderRadius:6,fontSize:12,color:"#1e293b",background:"#fff",cursor:"pointer"}}>
          <option value="todos">Todos</option>
          {asesores.map(a=><option key={a} value={a}>{a}</option>)}
        </select>
        {mostrarTendencia && (
          <span style={{fontSize:10,color:"#94a3b8",marginLeft:4}}>▲▼ comparando vs período anterior</span>
        )}
      </div>

      {/* KPIs — 6 cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:10,marginBottom:14}}>
        <KPI icono="📋" label="Total cotizaciones"    valor={stats.total}              sub={`${stats.perdidas.length} perdidas`}      subColor="#DC2626"
          curr={stats.total}        prev={statsP.total} />
        <KPI icono="✅" label="Cerradas"              valor={stats.cerradas.length}    sub={`${stats.conversion}% conversión`}
          curr={stats.cerradas.length} prev={statsP.cerradas.length} />
        <KPI icono="💰" label="Ingresos cerrados"     valor={fmtMoney(stats.ingresos)} sub="ventas confirmadas"
          curr={stats.ingresos}     prev={statsP.ingresos} />
        <KPI icono="🎯" label="Ticket promedio"       valor={fmtMoney(stats.ticketProm)} sub="por cotización cerrada" subColor="#8B5CF6"
          curr={stats.ticketProm}   prev={statsP.ticketProm} />
        <KPI icono="📊" label="Pipeline activo"       valor={fmtMoney(stats.pipelineVal)} sub={`${stats.pipeline.length} en proceso`} subColor="#D97706"
          curr={stats.pipelineVal}  prev={statsP.pipelineVal} />
        <KPI icono="❌" label="Valor perdido"          valor={fmtMoney(stats.perdidoVal)} sub={`${stats.perdidas.length} perdidas`}   subColor="#DC2626"
          curr={stats.perdidoVal}   prev={statsP.perdidoVal} />
      </div>

      {/* Ganancia por moneda */}
      <div style={{background:"#fff",border:"0.5px solid #e8ecf3",borderRadius:12,padding:"14px 18px",marginBottom:14}}>
        <div style={{fontSize:11,fontWeight:700,color:"#1e293b",textTransform:"uppercase",letterSpacing:".05em",marginBottom:10}}>
          Ganancia por moneda — cotizaciones cerradas
        </div>
        <div style={{display:"flex",gap:16,flexWrap:"wrap",alignItems:"stretch"}}>
          {/* PEN */}
          <div style={{flex:1,minWidth:180,background:"#F0FDF4",border:"1px solid #BBF7D0",borderRadius:10,padding:"12px 16px"}}>
            <div style={{fontSize:10,color:"#166534",fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",marginBottom:4}}>S/ Soles (PEN)</div>
            <div style={{fontSize:22,fontWeight:900,color:"#166534"}}>{fmtMoney(gananciaMoneda.pen.total)}</div>
            <div style={{fontSize:11,color:"#16a34a",marginTop:3}}>{gananciaMoneda.pen.count} cotización{gananciaMoneda.pen.count !== 1 ? "es" : ""} cerrada{gananciaMoneda.pen.count !== 1 ? "s" : ""}</div>
          </div>
          {/* USD */}
          <div style={{flex:1,minWidth:180,background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:10,padding:"12px 16px"}}>
            <div style={{fontSize:10,color:"#1D4ED8",fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",marginBottom:4}}>$ Dólares (USD)</div>
            {gananciaMoneda.usd.count > 0 ? (
              <>
                <div style={{fontSize:22,fontWeight:900,color:"#1D4ED8"}}>{fmtMoneyUSD(gananciaMoneda.usd.total)}</div>
                <div style={{fontSize:11,color:"#3B82F6",marginTop:3}}>{gananciaMoneda.usd.count} cotización{gananciaMoneda.usd.count !== 1 ? "es" : ""} cerrada{gananciaMoneda.usd.count !== 1 ? "s" : ""}</div>
              </>
            ) : (
              <div style={{fontSize:13,color:"#93C5FD",fontStyle:"italic",marginTop:4}}>Sin cotizaciones en USD aún</div>
            )}
          </div>
          {/* Total PEN equivalente si hay USD */}
          {gananciaMoneda.usd.count > 0 && (
            <div style={{flex:1,minWidth:180,background:"#FAFAFA",border:"1px solid #E5E7EB",borderRadius:10,padding:"12px 16px"}}>
              <div style={{fontSize:10,color:"#374151",fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",marginBottom:4}}>Total combinado (estimado S/)</div>
              <div style={{fontSize:11,color:"#6B7280",marginBottom:6}}>Los montos USD se convierten con el tipo de cambio de cada cotización</div>
              <div style={{fontSize:13,fontWeight:700,color:"#374151"}}>
                {fmtMoney(gananciaMoneda.pen.total)} + {fmtMoneyUSD(gananciaMoneda.usd.total)}
              </div>
              <div style={{fontSize:10,color:"#9CA3AF",marginTop:2}}>Ver TC por cotización en tabla detalle</div>
            </div>
          )}
        </div>
      </div>

      {/* Fila 1 — Embudo | Gráfico | Asesores */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>

        <div className="card-lift" style={card}>
          <div style={cardTitle}>Embudo de cotizaciones</div>
          {porEstado.map(({estado,count},i)=>(
            <BarraFunnel key={estado} label={estado} count={count} total={stats.total||1} mounted={mounted} delay={i*80}
              color={estado==="Nueva"?"#378ADD":estado==="Contactado"?"#7F77DD":estado==="Propuesta"?"#F97316":estado==="Negociación"?"#D97706":estado==="Ganada"?"#1D9E75":"#DC2626"}/>
          ))}
        </div>

        {/* Gráfico ingresos + conversión */}
        <div className="card-lift" style={card}>
          <div style={cardTitle}>
            Ingresos por mes (S/)
            <span style={{fontSize:9,color:"#94a3b8"}}>últimos 6 meses</span>
          </div>
          <div style={{display:"flex",alignItems:"flex-end",gap:5,height:90,marginBottom:6}}>
            {ingresosMes.map((m,i)=>(
              <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                <div style={{fontSize:7,color:"#64748b",fontWeight:600}}>{m.val>0?`${(m.val/1000).toFixed(0)}k`:"0"}</div>
                <div style={{
                  width:"100%",
                  height: mounted ? `${Math.max((m.val/maxVal)*100,m.val>0?8:2)}%` : "2%",
                  background:m.isActual?"#F97316":"#3A3C7A",
                  borderRadius:"3px 3px 0 0",
                  minHeight: mounted ? (m.val>0?8:2) : 2,
                  transition:`height 500ms cubic-bezier(0.23,1,0.32,1) ${i*80+150}ms`,
                }}/>
                <div style={{fontSize:7,color:"#94a3b8"}}>{m.mes}</div>
                {m.conv>0 && (
                  <div style={{fontSize:7,fontWeight:700,color:m.conv>=30?"#1D9E75":"#D97706"}}>
                    {m.conv}%
                  </div>
                )}
              </div>
            ))}
          </div>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"#64748b"}}>
            <span>Acumulado: <strong style={{color:"#1e293b"}}>{fmtMoney(ingresosMes.reduce((s,m)=>s+m.val,0))}</strong></span>
            <span style={{fontSize:9,color:"#94a3b8"}}>% = conversión del mes</span>
          </div>
        </div>

        {/* Asesores */}
        <div className="card-lift" style={card}>
          <div style={cardTitle}>Rendimiento por asesor</div>
          {porAsesor.length===0 ? (
            <div style={{textAlign:"center",color:"#94a3b8",fontSize:12,padding:16}}>Sin datos</div>
          ) : porAsesor.slice(0,5).map((a,i)=>{
            const [bg,col]=AV_COLORS[i%AV_COLORS.length].split("|");
            const conv=a.total>0?((a.cerradas/a.total)*100).toFixed(0):0;
            return (
              <div key={a.nombre} style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,paddingBottom:10,borderBottom:"0.5px solid #f1f5f9"}}>
                <div style={{width:28,height:28,borderRadius:"50%",background:bg,color:col,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,flexShrink:0}}>
                  {a.nombre.charAt(0)}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:600,color:"#1e293b"}}>{a.nombre}</div>
                  <div style={{fontSize:9,color:"#64748b"}}>
                    {a.cerradas} cerradas · <span style={{background:"#E1F5EE",color:"#0F6E56",padding:"1px 5px",borderRadius:5,fontSize:9,fontWeight:600}}>{conv}%</span>
                  </div>
                </div>
                <div style={{fontSize:11,fontWeight:700,color:"#1e293b"}}>{fmtMoney(a.valor)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Fila 2 — Top clientes | Proyectos | Estancadas */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>

        {/* Top clientes */}
        <div className="card-lift" style={card}>
          <div style={cardTitle}>🏆 Top clientes por ingresos</div>
          {topClientes.length===0 ? (
            <div style={{textAlign:"center",color:"#94a3b8",fontSize:12,padding:16}}>
              Sin cotizaciones cerradas en este período.
            </div>
          ) : topClientes.map((c,i)=>{
            const maxT=topClientes[0].total||1;
            const pct=(c.total/maxT)*100;
            const medalColor=i===0?"#F59E0B":i===1?"#94A3B8":i===2?"#B45309":"#CBD5E1";
            return (
              <div key={c.nombre} style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:11,fontWeight:800,color:medalColor,width:18}}>#{i+1}</span>
                    <div>
                      <div style={{fontSize:11,fontWeight:600,color:"#1e293b"}}>{c.nombre}</div>
                      <div style={{fontSize:9,color:"#94a3b8"}}>{c.empresa} · {c.count} cot.</div>
                    </div>
                  </div>
                  <span style={{fontSize:11,fontWeight:700,color:"#1e293b"}}>{fmtMoney(c.total)}</span>
                </div>
                <div style={{background:"#f1f5f9",borderRadius:4,height:5,overflow:"hidden"}}>
                  <div style={{background:medalColor,height:5,borderRadius:4,width:mounted?`${pct}%`:"0%",transition:`width 600ms cubic-bezier(0.23,1,0.32,1) ${i*80}ms`}}/>
                </div>
              </div>
            );
          })}
        </div>

        {/* Proyectos */}
        <div className="card-lift" style={card}>
          <div style={cardTitle}>Proyectos <span>por valor</span></div>
          {proyectos.length===0 ? (
            <div style={{textAlign:"center",color:"#94a3b8",fontSize:12,padding:16}}>
              Agrega proyectos a tus cotizaciones para verlos aquí.
            </div>
          ) : proyectos.map(c=>{
            const es=ESTADO_STYLE[c.estado]||ESTADO_STYLE["Pendiente"];
            return (
              <div key={c.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 0",borderBottom:"0.5px solid #f1f5f9"}}>
                <div>
                  <div style={{fontSize:12,fontWeight:600,color:"#1e293b"}}>{c.proyecto}</div>
                  <div style={{fontSize:10,color:"#94a3b8"}}>{c.clienteEmpresa||c.clienteNombre}</div>
                  {c.numeroCot&&<div style={{fontSize:9,color:"#3A3C7A",fontWeight:600}}>{c.numeroCot}</div>}
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:12,fontWeight:700,color:["Ganada","Aprobada"].includes(c.estado)?"#1D9E75":c.estado==="Perdida"?"#DC2626":"#D97706"}}>{fmtMoney(c.valor)}</div>
                  <span style={{fontSize:9,fontWeight:600,padding:"2px 7px",borderRadius:6,background:es.bg,color:es.color}}>{c.estado}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Estancadas */}
        <div className="card-lift" style={card}>
          <div style={cardTitle}>Cotizaciones estancadas <span style={{color:"#DC2626"}}>requieren atención</span></div>
          {estancadas.length===0 ? (
            <div style={{textAlign:"center",color:"#94a3b8",fontSize:12,padding:16}}>
              ¡Sin cotizaciones estancadas!
            </div>
          ) : estancadas.map(c=>{
            const f=c.actualizadoEn?.toDate?c.actualizadoEn.toDate():new Date(c.actualizadoEn||0);
            const dias=Math.round((ahora-f)/86400000);
            const es=ESTADO_STYLE[c.estado]||ESTADO_STYLE["Pendiente"];
            return (
              <div key={c.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 0",borderBottom:"0.5px solid #f1f5f9"}}>
                <div style={{width:26,height:26,borderRadius:"50%",background:"#FCEBEB",color:"#A32D2D",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,flexShrink:0}}>
                  {(c.clienteNombre||"?").charAt(0)}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:600,color:"#1e293b"}}>
                    {c.clienteNombre}
                    {c.numeroCot&&<span style={{fontSize:9,fontWeight:700,color:"#3A3C7A",background:"#EEF2FF",padding:"1px 5px",borderRadius:4,marginLeft:5}}>{c.numeroCot}</span>}
                  </div>
                  <div style={{fontSize:10,color:"#64748b"}}>
                    <span style={{background:es.bg,color:es.color,padding:"1px 5px",borderRadius:5,fontSize:9,fontWeight:600}}>{c.estado}</span>
                    {c.proyecto?` · ${c.proyecto}`:""}
                  </div>
                </div>
                <div style={{fontSize:11,fontWeight:700,color:"#DC2626",textAlign:"right",flexShrink:0}}>{dias}d sin cambio</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Historial — ancho completo */}
      <div style={{...card, marginBottom:12}}>
        <div style={cardTitle}>Historial cierres y pérdidas <span>editable</span></div>
        {histLoad ? (
          <div style={{textAlign:"center",color:"#94a3b8",fontSize:12,padding:16}}>Cargando...</div>
        ) : historialFiltrado.length===0 ? (
          <div style={{textAlign:"center",color:"#94a3b8",fontSize:12,padding:16}}>
            Cambia el estado de una cotización a Ganada o Perdida para ver el historial aquí.
          </div>
        ) : (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 24px",maxHeight:320,overflowY:"auto"}}>
            {historialFiltrado.map(h=>(
              <div key={h.id} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"9px 0",borderBottom:"0.5px solid #f1f5f9"}}>
                <div style={{width:8,height:8,borderRadius:"50%",background:h.estadoFinal==="Ganada"?"#1D9E75":"#DC2626",flexShrink:0,marginTop:4}}/>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:600,color:"#1e293b"}}>
                    {h.nombre} — <span style={{color:h.estadoFinal==="Ganada"?"#1D9E75":"#DC2626"}}>{h.estadoFinal}</span>
                  </div>
                  {h.numeroCot&&(
                    <div style={{fontSize:9,fontWeight:700,color:"#3A3C7A",background:"#EEF2FF",padding:"1px 6px",borderRadius:4,display:"inline-block",marginBottom:2}}>{h.numeroCot}</div>
                  )}
                  <div style={{fontSize:10,color:"#64748b",marginTop:1}}>
                    {fmtFecha(h.fecha)} · {h.responsable||"—"}{h.proyecto?` · ${h.proyecto}`:""}
                  </div>
                  {editId===h.id ? (
                    <div style={{marginTop:5}}>
                      <textarea value={editComent} onChange={e=>setEditComent(e.target.value)} rows={2}
                        style={{width:"100%",padding:"5px 8px",border:"1px solid #e2e8f0",borderRadius:5,fontSize:11,resize:"none",fontFamily:"inherit",boxSizing:"border-box"}}/>
                      <div style={{display:"flex",gap:5,marginTop:3}}>
                        <button onClick={async()=>{await actualizarHistorial(h.id,{comentario:editComent});setEditId(null);}}
                          style={{fontSize:10,padding:"3px 8px",borderRadius:4,background:"#3A3C7A",color:"#fff",border:"none",cursor:"pointer",fontWeight:600}}>Guardar</button>
                        <button onClick={()=>setEditId(null)}
                          style={{fontSize:10,padding:"3px 7px",borderRadius:4,background:"#f1f5f9",color:"#475569",border:"none",cursor:"pointer"}}>Cancelar</button>
                      </div>
                    </div>
                  ) : h.comentario ? (
                    <div style={{fontSize:10,color:"#475569",marginTop:3,fontStyle:"italic",background:"#f8fafc",padding:"3px 7px",borderRadius:4}}>"{h.comentario}"</div>
                  ) : null}
                  <div style={{display:"flex",gap:5,marginTop:4}}>
                    <button onClick={()=>{setEditId(h.id);setEditComent(h.comentario||"");}}
                      style={{fontSize:9,padding:"2px 7px",borderRadius:4,background:"#E6F1FB",color:"#185FA5",border:"none",cursor:"pointer"}}>✏ Editar</button>
                    {confirmDel===h.id ? (
                      <>
                        <button onClick={async()=>{await eliminarHistorial(h.id);setConfirmDel(null);}}
                          style={{fontSize:9,padding:"2px 7px",borderRadius:4,background:"#DC2626",color:"#fff",border:"none",cursor:"pointer",fontWeight:600}}>Confirmar</button>
                        <button onClick={()=>setConfirmDel(null)}
                          style={{fontSize:9,padding:"2px 7px",borderRadius:4,background:"#f1f5f9",color:"#475569",border:"none",cursor:"pointer"}}>Cancelar</button>
                      </>
                    ) : (
                      <button onClick={()=>setConfirmDel(h.id)}
                        style={{fontSize:9,padding:"2px 7px",borderRadius:4,background:"#FCEBEB",color:"#A32D2D",border:"none",cursor:"pointer"}}>🗑 Eliminar</button>
                    )}
                  </div>
                </div>
                <div style={{fontSize:12,fontWeight:700,color:h.estadoFinal==="Ganada"?"#1D9E75":"#DC2626",flexShrink:0}}>{fmtMoney(h.valor)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tabla detalle — toggle */}
      <div style={card}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom: showDetalle ? 12 : 0}}>
          <div style={cardTitle}>
            Detalle cotizaciones del período
            <span style={{fontSize:10,color:"#64748b",fontWeight:400,textTransform:"none",letterSpacing:0}}>
              {cotFiltradas.length} registros
            </span>
          </div>
          <button
            onClick={()=>setShowDetalle(v=>!v)}
            style={{fontSize:11,fontWeight:600,padding:"4px 12px",borderRadius:8,border:"1px solid #e2e8f0",background:"#fff",color:"#3A3C7A",cursor:"pointer",marginBottom: showDetalle ? 12 : 0}}
          >
            {showDetalle ? "Ocultar ▲" : "Ver tabla ▼"}
          </button>
        </div>

        {showDetalle && (
          <>
            <div style={{marginBottom:10}}>
              <input
                placeholder="Buscar en esta tabla..."
                value={busqDetalle} onChange={e=>setBusqDetalle(e.target.value)}
                style={{width:"100%",padding:"7px 12px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:13,outline:"none",boxSizing:"border-box"}}
              />
            </div>
            <div style={{overflowX:"auto",maxHeight:400,overflowY:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead style={{background:"#f8fafc",borderBottom:"1px solid #e2e8f0",position:"sticky",top:0}}>
                  <tr>
                    {["N° Cot.","Cliente","Empresa","Proyecto","Estado","Valor","Moneda","Responsable","Seguimiento"].map(h=>(
                      <th key={h} style={{textAlign:"left",padding:"8px 12px",fontSize:10,fontWeight:600,color:"#94a3b8",whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detalleRows.length===0 ? (
                    <tr><td colSpan={8} style={{textAlign:"center",padding:24,color:"#94a3b8"}}>Sin resultados</td></tr>
                  ) : detalleRows.map(c=>{
                    const es=ESTADO_STYLE[c.estado]||ESTADO_STYLE["Pendiente"];
                    return (
                      <tr key={c.id} style={{borderBottom:"0.5px solid #f1f5f9"}}>
                        <td style={{padding:"8px 12px"}}>
                          {c.numeroCot
                            ? <span style={{fontSize:10,fontWeight:700,color:"#3A3C7A",background:"#EEF2FF",padding:"1px 6px",borderRadius:4}}>{c.numeroCot}</span>
                            : <span style={{color:"#94a3b8"}}>—</span>}
                        </td>
                        <td style={{padding:"8px 12px",fontWeight:600,color:"#1e293b"}}>{c.clienteNombre||"—"}</td>
                        <td style={{padding:"8px 12px",color:"#64748b"}}>{c.clienteEmpresa||"—"}</td>
                        <td style={{padding:"8px 12px",color:"#475569",maxWidth:140,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.proyecto||"—"}</td>
                        <td style={{padding:"8px 12px"}}>
                          <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:8,background:es.bg,color:es.color}}>{c.estado}</span>
                        </td>
                        <td style={{padding:"8px 12px",fontWeight:700,color:["Ganada","Aprobada"].includes(c.estado)?"#1D9E75":c.estado==="Perdida"?"#DC2626":"#1e293b"}}>
                          {c.moneda==="USD" ? fmtMoneyUSD(c.valor) : fmtMoney(c.valor)}
                        </td>
                        <td style={{padding:"8px 12px"}}>
                          {c.moneda==="USD"
                            ? <span style={{background:"#EFF6FF",color:"#1D4ED8",fontSize:10,fontWeight:700,padding:"1px 7px",borderRadius:8}}>USD</span>
                            : <span style={{background:"#F0FDF4",color:"#166534",fontSize:10,fontWeight:700,padding:"1px 7px",borderRadius:8}}>PEN</span>
                          }
                          {c.moneda==="USD" && c.tipoCambio && (
                            <div style={{fontSize:9,color:"#93C5FD",marginTop:1}}>TC {c.tipoCambio}</div>
                          )}
                        </td>
                        <td style={{padding:"8px 12px",color:"#64748b"}}>{c.responsable||"—"}</td>
                        <td style={{padding:"8px 12px",color:"#475569"}}>{c.proximoSeguimiento||"—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{fontSize:10,color:"#94a3b8",marginTop:8,textAlign:"right"}}>
              {detalleRows.length} cotizaciones · Total: {fmtMoney(detalleRows.reduce((s,c)=>s+(Number(c.valor)||0),0))}
            </div>
          </>
        )}
      </div>

    </div>
  );
}
